import {
  featureGatedPermissions,
  usePermissionsForUserRoleAndType,
} from '@retail/auth/data-access';
import { Action, Resource } from '@retail/auth/types';
import { useSelectedOrgUnit } from './SelectedOrgUnit';
import { useCallback, useMemo } from 'react';
import { MgPrisFeature, OrgUnitType } from '@retail/mgpris/config-types';
import { useMgPrisConfig } from '@retail/app/stores/app-config';
import { isFeatureEnabled } from '@retail/utils';

export const usePermissionsForCurrentUser = () => {
  const { selectedOrgUnitAssortment, orgUnitType } = useSelectedOrgUnit();
  const { featureFlags } = useMgPrisConfig();
  const { userRole, appTenant } = selectedOrgUnitAssortment;
  const orgType = OrgUnitType.fromOrgUnitType(selectedOrgUnitAssortment.orgType);

  const basePermissions = usePermissionsForUserRoleAndType(userRole, appTenant, orgType);

  const userPermissions = useMemo(() => {
    const gatedPermissions = Object.entries(featureGatedPermissions)
      .filter(([name]) =>
        isFeatureEnabled({
          features: featureFlags,
          name: name as MgPrisFeature,
          orgUnitType,
          country: appTenant.countryCode,
        })
      )
      .flatMap(([, permissions]) => permissions ?? [])
      .filter(
        (perm) =>
          perm.userRole === userRole &&
          perm.orgType === orgType &&
          perm.appTenants.includes(appTenant.countryCode)
      );
    return [...basePermissions, ...gatedPermissions];
  }, [appTenant.countryCode, basePermissions, featureFlags, orgType, orgUnitType, userRole]);

  const userCanRead = useCallback(
    (resource: Resource) =>
      userPermissions.some((perm) => perm.resource === resource && perm.canRead()),
    [userPermissions]
  );

  const userCanCreate = useCallback(
    (resource: Resource) =>
      userPermissions.some((perm) => perm.resource === resource && perm.canCreate()),
    [userPermissions]
  );

  const userCanUpdate = useCallback(
    (resource: Resource) =>
      userPermissions.some((perm) => perm.resource === resource && perm.canUpdate()),
    [userPermissions]
  );

  const userCanDelete = useCallback(
    (resource: Resource) =>
      userPermissions.some((perm) => perm.resource === resource && perm.canDelete()),
    [userPermissions]
  );

  const getPermittedActions = useCallback(
    (resource: Resource): Action[] =>
      userPermissions.find((permission) => resource === permission.resource)?.actions ?? [],
    [userPermissions]
  );

  return useMemo(
    () => ({
      userPermissions,
      userCanRead,
      userCanCreate,
      userCanUpdate,
      userCanDelete,
      getPermittedActions,
    }),
    [getPermittedActions, userCanCreate, userCanDelete, userCanRead, userCanUpdate, userPermissions]
  );
};
